import { AfterViewInit, Component, inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { ActivatedRoute, Router } from '@angular/router';
import { distinctUntilChanged, map, Subscription, switchMap } from 'rxjs';
import { Car } from '../../../../models/car';
import { RentalType } from '../../../../models/enums';
import { CarService } from '../../../../services/vehicles/car-service';
import { GREEN_ZONES, RED_ZONES } from '../../../../constants/zones.constants';

const MAP_PARAMS = ['search', 'status', 'rentalType', 'modelId', 'needsRebalancing', 'minFuelLevel', 'maxFuelLevel'];

@Component({
  selector: 'app-cars-list-map',
  imports: [GoogleMapsModule],
  templateUrl: './cars-list-map.html',
  styleUrl: './cars-list-map.scss'
})
export class CarsListMap implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(GoogleMap) map!: GoogleMap;

  center: google.maps.LatLngLiteral = { lat: 25.2048, lng: 55.2708 };
  zoom = 10;
  mapId = 'yaldi-map-api';

  options: google.maps.MapOptions = {
    restriction: {
      latLngBounds: { north: 26.5, south: 22.5, west: 51.5, east: 56.5 },
      strictBounds: true
    },
    zoomControl: true,
    zoomControlOptions: {
      position: google.maps.ControlPosition.RIGHT_CENTER
    }
  };

  private readonly greenZones = GREEN_ZONES;
  private readonly redZones = RED_ZONES;

  private polygons: google.maps.Polygon[] = [];
  private cars: Car[] = [];
  private markers: google.maps.marker.AdvancedMarkerElement[] = [];
  private sub!: Subscription;
  private mapReady = false;

  private ngZone = inject(NgZone);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private carService = inject(CarService);

  ngOnInit() {
    this.sub = this.activatedRoute.queryParams.pipe(
      map(params => Object.fromEntries(
        Object.entries(params).filter(([key]) => MAP_PARAMS.includes(key))
      )),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      switchMap(params => this.carService.getCarsList({ ...params, size: 1000, sortBy: 'updatedAt', sortDirection: 'DESC' }))
    ).subscribe(res => {
      this.cars = res.data.content;
      if (this.mapReady) {
        this.clearMarkers();
        this.renderMarkers();
      }
    });
  }

  ngAfterViewInit() {
    this.mapReady = true;
    this.renderZones();
    this.renderMarkers();
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
    this.clearZones();
  }

  private renderZones() {
    if (!this.map?.googleMap) return;

    this.greenZones.forEach(points => {
      this.polygons.push(new google.maps.Polygon({
        paths: points,
        map: this.map.googleMap!,
        strokeColor: '#2e7d32',
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: '#4caf50',
        fillOpacity: 0.18,
        clickable: false,
        zIndex: 1,
      }));
    });

    this.redZones.forEach(points => {
      this.polygons.push(new google.maps.Polygon({
        paths: points,
        map: this.map.googleMap!,
        strokeColor: '#c62828',
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: '#f44336',
        fillOpacity: 0.18,
        clickable: false,
        zIndex: 1,
      }));
    });
  }

  private clearZones() {
    this.polygons.forEach(p => p.setMap(null));
    this.polygons = [];
  }

  private clearMarkers() {
    this.markers.forEach(m => m.map = null);
    this.markers = [];
  }

  private renderMarkers() {
    if (!this.map?.googleMap || !this.cars.length) return;

    const valid = this.cars.filter(car =>
      car.telemetry?.position?.latitude != null &&
      car.telemetry?.position?.longitude != null
    );

    valid.forEach(car => {
      const lat = car.telemetry!.position!.latitude;
      const lng = car.telemetry!.position!.longitude;

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat, lng },
        map: this.map.googleMap,
        title: `${car.model?.maker} ${car.model?.name} — ${car.plateNo}`,
        content: this.buildMarkerEl(car)
      });

      marker.addListener('gmp-click', () => {
        this.ngZone.run(() => this.router.navigate([`vehicles/${car.id}`]));
      });

      this.markers.push(marker);
    });

    this.fitBounds(valid);
  }

  private buildMarkerEl(car: Car): HTMLElement {
    const isSharing = car.model?.rentalType === RentalType.CAR_SHARING;

    const pinSrc = isSharing ? 'assets/icons/sharing-location.svg' : 'assets/icons/rental-location.svg';
    const carSrc = isSharing ? 'assets/icons/car-location-sharing.svg' : 'assets/icons/car-location-rental.svg';

    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'position:relative;width:34px;height:52px;cursor:pointer;';

    const pin = document.createElement('img');
    pin.src = pinSrc;
    pin.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;';

    const whiteBg = document.createElement('div');
    whiteBg.style.cssText = 'position:absolute;width:26px;height:26px;top:6px;left:50%;transform:translateX(-50%);background:#fff;border-radius:50%;';

    const carIcon = document.createElement('img');
    carIcon.src = carSrc;
    carIcon.style.cssText = 'position:absolute;width:22px;height:22px;top:7px;left:50%;transform:translateX(-50%);';

    wrapper.appendChild(pin);
    wrapper.appendChild(whiteBg);
    wrapper.appendChild(carIcon);
    return wrapper;
  }

  private fitBounds(cars: Car[]) {
    if (!this.map?.googleMap || !cars.length) return;
    const bounds = new google.maps.LatLngBounds();
    cars.forEach(c => bounds.extend({
      lat: c.telemetry!.position!.latitude,
      lng: c.telemetry!.position!.longitude
    }));
    this.map.googleMap.fitBounds(bounds);
  }
}
